import React, { useEffect, useMemo, useRef } from "react";
import { useProvetContext, useTranslationContext } from "../../context";
import { useFocusAfterReload } from "../../hooks/useFocusAfterReload";
import { ProvetDialogStep, SelectedAppointmentTime } from "../../types";
import { isConnectionError } from "../../utilities/apiClient";
import { AppointmentTimeList } from "./appointmentTimeList";
import { CalendarGrid } from "./calendarGrid";
import { CalendarHeader } from "./calendarHeader";
import { useAppointmentPickerHandlers } from "./hooks/useAppointmentPickerHandlers";
import { useAppointmentTimeError } from "./hooks/useAppointmentTimeError";
import { useAvailableVeterinarians } from "./hooks/useAvailableVeterinarians";
import { useCalendarState } from "./hooks/useCalendarState";
import { useCalendarUpdater } from "./hooks/useCalendarUpdater";
import { useRequestManager } from "./hooks/useRequestManager";
import { useTimeSlotFocus } from "./hooks/useTimeSlotFocus";
import { VeterinarianSelect } from "./veterinarianSelect";

interface ProvetAppointmentTimePickerProps {
  onSelectAppointmentTime?: (appointmentTime?: SelectedAppointmentTime) => void;
}

/**
 * Appointment time picker component for selecting date and time for veterinary appointments.
 *
 * Features:
 * - Interactive calendar grid for date selection
 * - Month/year navigation with disabled past dates; it waits while the times load
 * - Available appointment time slots for selected date
 * - Optional veterinarian selection (if enabled by department)
 * - Automatic refresh when department or reason changes, and whenever Step 3 reopens
 *   ("Change time", or a time that was taken in the meantime)
 * - Loading states for async operations
 * - An error with "Try again" when the times fail to load
 * - Auto-focus on first time slot when loaded
 * - Error handling for unavailable time slots
 *
 * The component manages its own calendar state and coordinates multiple hooks for:
 * - Calendar state management (dates, times, veterinarians)
 * - Request management (debouncing, cancellation)
 * - Calendar updates (fetching available times)
 * - Event handling (date changes, time selection)
 * - Accessibility (focus management)
 *
 * @param onSelectAppointmentTime - Optional callback invoked when user selects an appointment time.
 *                                   Receives the selected SelectedAppointmentTime object, or undefined if cleared.
 *
 * @example
 * ```tsx
 * <ProvetAppointmentTimePicker
 *   onSelectAppointmentTime={(appointmentTime) => {
 *     if (appointmentTime) {
 *       console.log('Selected:', appointmentTime.availableTime);
 *       handleTimeSelection(appointmentTime);
 *     }
 *   }}
 * />
 * ```
 */
export const ProvetAppointmentTimePicker = ({
  onSelectAppointmentTime
}: ProvetAppointmentTimePickerProps) => {
  const provetContext = useProvetContext();
  const translationContext = useTranslationContext();
  const calendarState = useCalendarState({ language: provetContext.language });
  const requestManager = useRequestManager();
  const { updateSelectedDate, showTimesForSelectedVet, retry } = useCalendarUpdater({
    provetContext,
    calendarState,
    requestManager
  });
  const { errorSelectTimeNoLongerAvailable, showError } = useAppointmentTimeError();

  const loadingTimes = calendarState.timesStatus === "loading";
  const { targetRef: timesRef, retry: retryTimes } = useFocusAfterReload(
    calendarState.timesStatus,
    retry
  );
  // A lost connection or a timeout keeps its own error, so ErrorMessage shows its readable
  // text; any other failure says the times couldn't be loaded
  const loadError = useMemo(() => {
    if (calendarState.timesStatus !== "error") {
      return null;
    }

    if (
      calendarState.timesLoadError instanceof Error &&
      isConnectionError(calendarState.timesLoadError)
    ) {
      return calendarState.timesLoadError;
    }

    return new Error(translationContext?.translations?.errorLoadingAvailableTimes ?? "");
  }, [
    calendarState.timesStatus,
    calendarState.timesLoadError,
    translationContext?.translations?.errorLoadingAvailableTimes
  ]);

  // Computed properties
  const currentAvailableVeterinarians = useAvailableVeterinarians(
    calendarState.provetMonth
  );
  const selectedYear = calendarState.selectedDate.getFullYear();
  const selectedMonth = calendarState.selectedDate.getMonth();
  const calendarDays = calendarState.provetMonth?.weeks.flatMap(week => week.days) ?? [];

  // Nothing before this month can be shown
  const today = new Date();
  const previousMonthButtonDisabled =
    selectedYear < today.getFullYear() ||
    (selectedYear === today.getFullYear() && selectedMonth <= today.getMonth());
  const previousYearButtonDisabled = selectedYear <= today.getFullYear();

  const currentMonthHasAvailableTimes = calendarDays.some(
    day =>
      day.date.getFullYear() === selectedYear &&
      day.date.getMonth() === selectedMonth &&
      (calendarState.provetMonth?.getAppointmentsForDate(
        day.date,
        calendarState.selectedVetId
      )?.length || 0) > 0
  );

  const firstAvailableDate = currentMonthHasAvailableTimes
    ? undefined
    : calendarState.firstAvailableDate;

  const selectedMonthLabel = calendarState.selectedDate.toLocaleDateString(
    calendarState.currentLocale,
    { month: "long" }
  );

  // Different messages for "no available times" depending on whether the current month has
  // available times at all or not
  const noAvailableTimesMessage = currentMonthHasAvailableTimes
    ? translationContext?.translations?.noAvailableTimes
    : translationContext?.translations?.noAvailableTimesInMonth?.replace(
        "{month}",
        selectedMonthLabel
      ) || `No available times in ${selectedMonthLabel}`;

  // Use custom hook for event handlers
  const {
    monthChanged,
    yearChanged,
    dateChanged,
    veterinarianSelected,
    availableAppointmentTimeSelected
  } = useAppointmentPickerHandlers({
    calendarState,
    updateSelectedDate,
    showTimesForSelectedVet,
    provetContext,
    onSelectAppointmentTime,
    currentAvailableVeterinarians,
    requestManager,
    showError
  });

  // Auto-focus first time slot when times load after date selection
  useTimeSlotFocus({
    availableAppointmentTimes: calendarState.availableAppointmentTimesForSelectedDay,
    loadingAvailableTimes: loadingTimes,
    selectedDate: calendarState.selectedDate
  });

  const selectedDepartment = provetContext?.bookingFlowState.formData?.selectedDepartment;
  const selectedReason = provetContext?.bookingFlowState.formData?.selectedReason;
  const isStepOpen =
    provetContext?.bookingFlowState.activeStep === ProvetDialogStep.SelectDateAndTime;

  // Whether the times may have changed since they were loaded: set when Step 3 closes
  const timesOutdatedRef = useRef(false);

  // A new clinic or treatment: start again from today. Declared before the effect below, so
  // when both apply the times are loaded once.
  useEffect(() => {
    const departmentChanged =
      calendarState.previousDepartmentRef.current?.id !== selectedDepartment?.id;
    const reasonChanged =
      calendarState.previousReasonRef.current?.id !== selectedReason?.id;

    calendarState.previousDepartmentRef.current = selectedDepartment || null;
    calendarState.previousReasonRef.current = selectedReason || null;

    if (selectedDepartment && selectedReason && (departmentChanged || reasonChanged)) {
      timesOutdatedRef.current = false;
      const startDate = new Date();
      calendarState.setSelectedDate(startDate);
      calendarState.setSelectedVetId(0);
      calendarState.setTimesStatus("loading");
      updateSelectedDate(startDate, { refetch: true, selectFirstAvailableDay: true });
    }
  }, [selectedDepartment, selectedReason]);

  // Step 3 reopened ("Change time", or the chosen time was taken): the picker stays mounted
  // while closed, so load the times again instead of showing ones that may be gone
  // (observation 12). The selected date and vet are kept.
  useEffect(() => {
    if (!isStepOpen) {
      timesOutdatedRef.current = true;
      return;
    }

    if (timesOutdatedRef.current && selectedDepartment && selectedReason) {
      timesOutdatedRef.current = false;
      calendarState.setTimesStatus("loading");
      updateSelectedDate(calendarState.selectedDate, { refetch: true });
    }
  }, [isStepOpen]);

  useEffect(
    () => () => {
      requestManager.clearTimer();
      requestManager.cancelPreviousRequest();
    },
    [requestManager.clearTimer, requestManager.cancelPreviousRequest]
  );

  return (
    <div className="flex flex-1 flex-col gap-xl">
      <VeterinarianSelect
        items={currentAvailableVeterinarians}
        selectedVetId={calendarState.selectedVetId}
        onSelectedChanged={veterinarianSelected}
        visible={
          !provetContext?.bookingFlowState.formData?.selectedDepartment
            ?.hide_veterinarian_names && currentAvailableVeterinarians.length > 0
        }
      />
      <CalendarHeader
        selectedDate={calendarState.selectedDate}
        currentLocale={calendarState.currentLocale}
        onMonthChange={monthChanged}
        onYearChange={yearChanged}
        onTodayClick={() => dateChanged(new Date())}
        previousMonthButtonDisabled={previousMonthButtonDisabled}
        previousYearButtonDisabled={previousYearButtonDisabled}
        busy={loadingTimes}
        translations={translationContext?.translations}
      />
      {calendarState.provetMonth?.weeks && (
        <CalendarGrid
          weeks={calendarState.provetMonth.weeks.map(w => w.days)}
          selectedDate={calendarState.selectedDate}
          selectedVetId={calendarState.selectedVetId}
          onDateChange={dateChanged}
        />
      )}
      {/* A stable focus target for after "Try again" */}
      <div
        ref={timesRef}
        tabIndex={-1}
        className="outline-none"
        data-online-booking-calendar-times
      >
        <AppointmentTimeList
          loading={loadingTimes}
          loadError={loadError}
          onRetry={retryTimes}
          appointmentTimes={calendarState.availableAppointmentTimesForSelectedDay}
          onSelectTime={availableAppointmentTimeSelected}
          errorSelectTimeNoLongerAvailable={errorSelectTimeNoLongerAvailable}
          noAvailableTimesMessage={noAvailableTimesMessage}
          firstAvailableDate={firstAvailableDate}
          onSelectFirstAvailableDate={() => {
            if (firstAvailableDate) {
              dateChanged(firstAvailableDate);
            }
          }}
          firstAvailableTimeLabel={
            translationContext?.translations?.goToFirstAvailableTime
          }
        />
      </div>
    </div>
  );
};

export default ProvetAppointmentTimePicker;
